import { useState } from 'react';
import { Plus, X, Users, Calendar, Home, Package } from 'lucide-react';
import { DURATION_TYPES, ROOM_AMENITIES } from './constants';

const StepPackages = ({ formData, handleInputChange }) => {
  const [packages, setPackages] = useState(formData.packages || []);
  const [editingPackageIndex, setEditingPackageIndex] = useState(null);

  const addPackage = () => {
    const newPackage = {
      id: Date.now().toString(),
      name: '',
      description: '',
      durationType: 'fixed',
      durationNights: 3,
      durationMin: 3,
      durationMax: 7,
      minGuests: 1,
      maxGuests: 10,
      price: formData.basePrice || formData.price || 0,
      rooms: [],
    };
    setPackages([...packages, newPackage]);
    setEditingPackageIndex(packages.length);
    handleInputChange('packages', [...packages, newPackage]);
  };

  const updatePackage = (index, field, value) => {
    const updated = [...packages];
    updated[index] = { ...updated[index], [field]: value };
    setPackages(updated);
    handleInputChange('packages', updated);
  };

  const removePackage = (index) => {
    const updated = packages.filter((_, i) => i !== index);
    setPackages(updated);
    handleInputChange('packages', updated);
    if (editingPackageIndex === index) {
      setEditingPackageIndex(null);
    } else if (editingPackageIndex > index) {
      setEditingPackageIndex(editingPackageIndex - 1);
    }
  };

  const addRoom = (packageIndex) => {
    const updated = [...packages];
    if (!updated[packageIndex].rooms) {
      updated[packageIndex].rooms = [];
    }
    updated[packageIndex].rooms.push({
      id: Date.now().toString(),
      name: '',
      description: '',
      maxOccupancy: 2,
      amenities: [],
    });
    setPackages(updated);
    handleInputChange('packages', updated);
  };

  const updateRoom = (packageIndex, roomIndex, field, value) => {
    const updated = [...packages];
    updated[packageIndex].rooms[roomIndex] = {
      ...updated[packageIndex].rooms[roomIndex],
      [field]: value,
    };
    setPackages(updated);
    handleInputChange('packages', updated);
  };

  const toggleRoomAmenity = (packageIndex, roomIndex, amenityId) => {
    const updated = [...packages];
    const room = updated[packageIndex].rooms[roomIndex];
    const amenities = room.amenities || [];
    if (amenities.includes(amenityId)) {
      room.amenities = amenities.filter((id) => id !== amenityId);
    } else {
      room.amenities = [...amenities, amenityId];
    }
    setPackages(updated);
    handleInputChange('packages', updated);
  };

  const removeRoom = (packageIndex, roomIndex) => {
    const updated = [...packages];
    updated[packageIndex].rooms = updated[packageIndex].rooms.filter((_, i) => i !== roomIndex);
    setPackages(updated);
    handleInputChange('packages', updated);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Packages</h3>
          <p className="text-sm text-gray-600">Create packages with different durations and accommodation options</p>
        </div>
        <button
          type="button"
          onClick={addPackage}
          className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm font-medium"
        >
          <Plus className="w-4 h-4" />
          Add Package
        </button>
      </div>

      {packages.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
          <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">No packages yet. Create your first package to get started.</p>
          <button
            type="button"
            onClick={addPackage}
            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Package
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {packages.map((pkg, pkgIndex) => (
            <div key={pkg.id} className="border-2 border-gray-200 rounded-lg p-6">
              <div className="flex items-start justify-between mb-4">
                <h4 className="text-base font-medium text-gray-900">Package {pkgIndex + 1}</h4>
                <button
                  type="button"
                  onClick={() => removePackage(pkgIndex)}
                  className="text-red-600 hover:text-red-800"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Package Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Package Name * (50-100 characters)
                  </label>
                  <input
                    type="text"
                    required
                    maxLength={100}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 bg-white/50"
                    placeholder="3-Day Eco Yoga Retreat"
                    value={pkg.name}
                    onChange={(e) => updatePackage(pkgIndex, 'name', e.target.value)}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    {pkg.name.length}/100 characters
                  </p>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description * (300-1000 words recommended)
                  </label>
                  <textarea
                    rows={6}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 bg-white/50 resize-none"
                    placeholder="Describe the package itinerary, inclusions (meals, activities, transfers), and what makes it special..."
                    value={pkg.description}
                    onChange={(e) => updatePackage(pkgIndex, 'description', e.target.value)}
                  />
                  <p className={`mt-1 text-xs ${
                    pkg.description.split(/\s+/).filter(Boolean).length < 300
                      ? 'text-amber-600'
                      : pkg.description.split(/\s+/).filter(Boolean).length > 1000
                      ? 'text-amber-600'
                      : 'text-gray-500'
                  }`}>
                    {pkg.description.split(/\s+/).filter(Boolean).length.toLocaleString()} words (recommended: 300-1000) • {pkg.description.length.toLocaleString()} characters
                  </p>
                </div>

                {/* Duration */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-emerald-600" />
                    Duration *
                  </label>
                  <select
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 bg-white/50 mb-3"
                    value={pkg.durationType}
                    onChange={(e) => updatePackage(pkgIndex, 'durationType', e.target.value)}
                  >
                    {DURATION_TYPES.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                  {pkg.durationType === 'fixed' ? (
                    <div>
                      <input
                        type="number"
                        min="1"
                        required
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 bg-white/50"
                        placeholder="3"
                        value={pkg.durationNights}
                        onChange={(e) => updatePackage(pkgIndex, 'durationNights', parseInt(e.target.value))}
                      />
                      <p className="mt-1 text-xs text-gray-500">Number of nights</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <input
                          type="number"
                          min="1"
                          required
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 bg-white/50"
                          placeholder="3"
                          value={pkg.durationMin}
                          onChange={(e) => updatePackage(pkgIndex, 'durationMin', parseInt(e.target.value))}
                        />
                        <p className="mt-1 text-xs text-gray-500">Min nights</p>
                      </div>
                      <div>
                        <input
                          type="number"
                          min="1"
                          required
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 bg-white/50"
                          placeholder="7"
                          value={pkg.durationMax}
                          onChange={(e) => updatePackage(pkgIndex, 'durationMax', parseInt(e.target.value))}
                        />
                        <p className="mt-1 text-xs text-gray-500">Max nights</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Guest Limits */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Users className="w-4 h-4 text-emerald-600" />
                      Min Guests *
                    </label>
                    <input
                      type="number"
                      min="1"
                      required
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 bg-white/50"
                      placeholder="2"
                      value={pkg.minGuests}
                      onChange={(e) => updatePackage(pkgIndex, 'minGuests', parseInt(e.target.value))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Max Guests *
                    </label>
                    <input
                      type="number"
                      min="1"
                      required
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 bg-white/50"
                      placeholder="20"
                      value={pkg.maxGuests}
                      onChange={(e) => updatePackage(pkgIndex, 'maxGuests', parseInt(e.target.value))}
                    />
                  </div>
                </div>

                {/* Price */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Price per person ($) *
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 bg-white/50"
                    placeholder="0.00"
                    value={pkg.price}
                    onChange={(e) => updatePackage(pkgIndex, 'price', parseFloat(e.target.value))}
                  />
                </div>

                {/* Rooms/Accommodation Options */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-gray-700 flex items-center gap-2">
                      <Home className="w-4 h-4 text-emerald-600" />
                      Room/Accommodation Options
                    </label>
                    <button
                      type="button"
                      onClick={() => addRoom(pkgIndex)}
                      className="inline-flex items-center gap-1 px-3 py-1 text-sm text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50 rounded-lg transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      Add Room
                    </button>
                  </div>

                  {(!pkg.rooms || pkg.rooms.length === 0) ? (
                    <p className="text-sm text-gray-500 italic">No rooms added yet</p>
                  ) : (
                    <div className="space-y-4">
                      {pkg.rooms.map((room, roomIndex) => (
                        <div key={room.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                          <div className="flex items-start justify-between mb-3">
                            <h5 className="text-sm font-medium text-gray-900">Room {roomIndex + 1}</h5>
                            <button
                              type="button"
                              onClick={() => removeRoom(pkgIndex, roomIndex)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>

                          <div className="space-y-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Room Name *
                              </label>
                              <input
                                type="text"
                                required
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 bg-white"
                                placeholder="Single Room, Shared Dorm, Glamping Tent"
                                value={room.name}
                                onChange={(e) => updateRoom(pkgIndex, roomIndex, 'name', e.target.value)}
                              />
                            </div>

                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Description
                              </label>
                              <textarea
                                rows={2}
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 bg-white resize-none"
                                placeholder="Describe the room type, features, and atmosphere..."
                                value={room.description}
                                onChange={(e) => updateRoom(pkgIndex, roomIndex, 'description', e.target.value)}
                              />
                              <p className="mt-1 text-xs text-gray-500">
                                {room.description.length.toLocaleString()} characters
                              </p>
                            </div>

                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Max Occupancy *
                              </label>
                              <input
                                type="number"
                                min="1"
                                required
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 bg-white"
                                placeholder="2"
                                value={room.maxOccupancy}
                                onChange={(e) => updateRoom(pkgIndex, roomIndex, 'maxOccupancy', parseInt(e.target.value))}
                              />
                            </div>

                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-2">
                                Room Amenities
                              </label>
                              <div className="grid grid-cols-2 gap-2">
                                {ROOM_AMENITIES.map((amenity) => {
                                  const isSelected = (room.amenities || []).includes(amenity.id);
                                  return (
                                    <label
                                      key={amenity.id}
                                      className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-gray-100"
                                    >
                                      <input
                                        type="checkbox"
                                        className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                                        checked={isSelected}
                                        onChange={() => toggleRoomAmenity(pkgIndex, roomIndex, amenity.id)}
                                      />
                                      <span className="text-xs text-gray-700">{amenity.label}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StepPackages;

